
import { io, Socket } from 'socket.io-client';
import { getToken } from './token-storage';

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10; // Aumentato a 10 per Telegram Mini App
  private currentUserId: number | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private connectionStable = false;

  connect(userId?: number) {
    console.log('[SOCKET] 🚀🚀🚀 connect() CALLED 🚀🚀🚀');
    console.log('[SOCKET] Current socket state:', {
      exists: !!this.socket,
      connected: this.socket?.connected,
      id: this.socket?.id,
      currentUserId: this.currentUserId,
      requestedUserId: userId
    });

    if (this.socket?.connected) {
      console.log('[SOCKET] Already connected, socket ID:', this.socket.id);
      
      // Se userId è fornito e diverso, aggiorna la room
      if (userId && userId !== this.currentUserId) {
        this.joinUserRoom(userId);
      }
      return;
    }

    const token = getToken();
    console.log('[SOCKET] 🔑 Token check result:', {
      hasToken: !!token,
      tokenLength: token?.length,
      tokenPreview: token?.substring(0, 20) + '...'
    });

    if (!token) {
      console.error('[SOCKET] ❌ NO TOKEN AVAILABLE - CANNOT CONNECT!');
      console.error('[SOCKET] ❌ This will prevent WebSocket connection!');
      return;
    }

    // Use window.location.origin for WebSocket connection - sempre l'URL corrente
    const baseUrl = window.location.origin;
    console.log('[SOCKET] 🔌🔌🔌 STARTING NEW CONNECTION 🔌🔌🔌');
    console.log('[SOCKET] Target URL:', baseUrl);
    console.log('[SOCKET] Transport:', 'websocket with polling fallback');
    console.log('[SOCKET] User ID:', userId);

    console.log('[SOCKET] 🏗️ Creating Socket.IO instance...');
    this.socket = io(baseUrl, {
      auth: { token },
      transports: ['websocket', 'polling'], // Allow polling fallback for Telegram/Replit environments
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
      autoConnect: true,
      forceNew: true, // Forza nuova connessione
      timeout: 20000, // Timeout aumentato a 20s per Telegram
    });

    console.log('[SOCKET] ✅ Socket.IO instance created');
    console.log('[SOCKET] 📦 Configuration:', {
      baseUrl,
      hasAuth: !!token,
      transports: ['websocket', 'polling'],
      autoConnect: true,
      forceNew: true,
      timeout: 20000
    });
    console.log('[SOCKET] Socket object:', {
      id: this.socket.id,
      connected: this.socket.connected,
      disconnected: this.socket.disconnected
    });

    this.socket.on('connect', () => {
      console.log('[SOCKET] ✅✅✅ CONNECTED TO SERVER ✅✅✅');
      console.log('[SOCKET] 🔌 Transport:', this.socket?.io?.engine?.transport?.name);
      console.log('[SOCKET] 🆔 Socket ID:', this.socket?.id);
      console.log('[SOCKET] 🌐 Connected:', this.socket?.connected);
      
      this.reconnectAttempts = 0;
      this.connectionStable = false; // Reset stability flag
      
      // Join user room se userId è disponibile
      if (userId) {
        this.joinUserRoom(userId);
      } else {
        console.warn('[SOCKET] ⚠️ No userId provided, cannot join room');
      }

      // Avvia heartbeat per mantenere connessione viva
      this.startHeartbeat();

      // Segna connessione come stabile dopo 3 secondi
      setTimeout(() => {
        if (this.socket?.connected) {
          this.connectionStable = true;
          console.log('[SOCKET] ✅ Connection marked as STABLE');
        }
      }, 3000);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[SOCKET] ❌ Disconnected:', reason);
      console.log('[SOCKET] 📊 Reconnect attempts:', this.reconnectAttempts);
      this.currentUserId = null;
      this.connectionStable = false;
      this.stopHeartbeat();
    });

    this.socket.on('connect_error', (error) => {
      console.error('[SOCKET] ❌ Connection error:', error);
      console.error('[SOCKET] 📊 Error details:', {
        message: error.message,
        description: (error as any).description,
        type: (error as any).type,
        transport: this.socket?.io?.engine?.transport?.name
      });
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.log('[SOCKET] ⚠️ Max reconnection attempts reached');
        console.log('[SOCKET] 🚨 TELEGRAM MINI APP: WebSocket persistently failing - this might be a Telegram limitation');
        // Non disconnettere - lascia che Socket.IO continui a riprovare in background
      }
    });

    // Listener per conferma join room
    this.socket.on('room-joined', (data) => {
      console.log('[SOCKET] 🎯 Room joined confirmation:', data);
      console.log('[SOCKET] 📊 Socket ID:', this.socket?.id);
      console.log('[SOCKET] 🆔 User ID:', this.currentUserId);
      console.log('[SOCKET] 🚪 Room:', `user:${this.currentUserId}`);
    });

    // LISTENER DEDICATO per profile-view events
    this.socket.on('profile-view', (data) => {
      console.log('[SOCKET] 👁️ PROFILE-VIEW EVENT RECEIVED:', data);
      console.log('[SOCKET] 📦 Event details:', {
        viewerUsername: data?.viewerUsername,
        viewedUserId: data?.viewedUserId,
        timestamp: new Date().toISOString()
      });
    });

    // Log per debug eventi ricevuti
    this.socket.onAny((eventName, ...args) => {
      console.log(`[SOCKET] 📨 Event received: "${eventName}"`, args);
      console.log(`[SOCKET] 📊 Event timestamp:`, new Date().toISOString());
    });

    // Listener per pong del heartbeat
    this.socket.on('pong', () => {
      console.log('[SOCKET] 💓 Heartbeat pong received');
    });
  }

  private startHeartbeat() {
    // Stop existing heartbeat se presente
    this.stopHeartbeat();

    console.log('[SOCKET] 💓 Starting heartbeat (ping every 25s)');
    
    // Invia ping ogni 25 secondi
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        console.log('[SOCKET] 💓 Sending heartbeat ping...');
        this.socket.emit('ping');
      } else {
        console.warn('[SOCKET] ⚠️ Cannot send heartbeat - socket not connected');
      }
    }, 25000); // 25 secondi
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      console.log('[SOCKET] 💔 Stopping heartbeat');
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private joinUserRoom(userId: number) {
    if (!this.socket) {
      console.warn('[SOCKET] ❌ Cannot join room - socket not available');
      return;
    }

    const roomName = `user:${userId}`;
    console.log('[SOCKET] 🚪🚪🚪 JOINING ROOM:', roomName);
    console.log('[SOCKET] 📡 Emitting join-room event...');
    console.log('[SOCKET] 🔍 Socket state:', {
      exists: !!this.socket,
      connected: this.socket.connected,
      id: this.socket.id
    });
    
    // Emetti l'evento anche se connected non è ancora true - il server lo gestirà quando sarà pronto
    this.socket.emit('join-room', roomName);
    this.currentUserId = userId;
    
    console.log('[SOCKET] ✅ Room join request sent for userId:', userId);
    console.log('[SOCKET] 💾 Current user ID stored:', this.currentUserId);
  }

  disconnect() {
    this.stopHeartbeat();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      console.log('[SOCKET] Disconnected');
    }
  }

  on(event: string, callback: (...args: any[]) => void) {
    if (this.socket) {
      console.log(`[SOCKET] 📝 Registering listener for event: "${event}"`);
      this.socket.on(event, callback);
    } else {
      console.warn(`[SOCKET] ⚠️ Cannot register listener for "${event}" - socket not available`);
    }
  }

  off(event: string, callback?: (...args: any[]) => void) {
    if (this.socket) {
      console.log(`[SOCKET] 🗑️ Removing listener for event: "${event}"`);
      this.socket.off(event, callback);
    }
  }

  emit(event: string, data?: any) {
    if (this.socket?.connected) {
      console.log(`[SOCKET] 📤 Emitting event: "${event}"`, data);
      this.socket.emit(event, data);
    } else {
      console.warn(`[SOCKET] ⚠️ Cannot emit "${event}" - socket not connected`);
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  isStable(): boolean {
    return this.connectionStable && this.isConnected();
  }

  getCurrentRoom(): string | null {
    if (!this.currentUserId) return null;
    return `user:${this.currentUserId}`;
  }

  getSocketId(): string | undefined {
    return this.socket?.id;
  }
}

export const socketService = new SocketService();
